<template>
	<div class="flow-popover" :open="open" tabindex="0" @keydown.esc="handleEscapeClick" @click.stop>
		<div
			:open="open"
			:transparent="!overlay"
			class="flow-popover-overlay"
			@click.stop="handleOverlayClick($event)"
		/>
		<!-- @slot Used to display triggers element on which popover will be displayed -->
		<slot />
		<div v-show="open" ref="popOver" class="flow-popover-content">
			<!-- @slot Used to have Popover content-->
			<slot v-if="open" name="content" />
		</div>
	</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import { createPopper, Placement } from "@popperjs/core";

export default defineComponent({
	name: "PopOver",

	provide() {
		return {
			getPopperInstance: () => {
				return this.popper;
			}
		};
	},

	props: {
		/**
		 * Do you want to display overlay ? <br/>
		 * true | false
		 */
		overlay: {
			type: Boolean,
			default: true
		},
		/**
		 * true | false
		 */
		open: {
			type: Boolean,
			default: true
		},
		/**
		 * 'auto' | 'auto-start' | 'auto-end' | 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'right' | 'right-start' | 'right-end' | 'left' | 'left-start' | 'left-end'
		 */
		placement: {
			type: String as PropType<Placement>,
			default: "right"
		},
		/**
		 * true | false
		 */
		escToClose: {
			type: Boolean,
			default: true
		},
		strategy: {
			type: String as PropType<"fixed" | "absolute">,
			default: "fixed"
			// validator(value: "fixed" | "absolute") {
			// 	return ["fixed", "absolute"].includes(value);
			// }
		},
		/**
		 * fallback placement is used when default placement position not available or unable render <br/>
		 *
		 */
		fallbackPlacements: {
			type: Array,
			default: () => ["left", "bottom", "top", "auto"]
		},
		/**
		 * DOM query selector of element <br/> e.g. '#target-element'
		 */
		target: {
			type: String,
			default: null
		},
		/**
		 * to make absolute target
		 */
		absoluteTarget: {
			type: Boolean,
			default: false
		},
		/**
		 * disable/enable auto update positioning based on content
		 */
		autoUpdate: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			popper: null as null | ReturnType<typeof createPopper>,
			mutationObserver: null as null | InstanceType<typeof MutationObserver>,
			resizeObserver: null as null | InstanceType<typeof ResizeObserver>,
			isUpdating: false,
			frameRequest: null as null | number
		};
	},
	watch: {
		open: {
			handler() {
				this.render(false);
			}
		},
		placement: {
			handler() {
				this.$nextTick(() => {
					this.render();
				});
			}
		},
		target: {
			handler(_newTarget, oldTarget) {
				this.removeTargetZIndex(oldTarget);
				this.$nextTick(() => {
					this.render();
				});
			}
		}
	},
	mounted() {
		this.render();

		this.resizeObserver = new ResizeObserver(() => {
			if (this.autoUpdate) {
				this.updatePopover();
			}
		});

		this.resizeObserver.observe(this.$refs.popOver as HTMLDivElement);

		this.mutationObserver = new MutationObserver(() => {
			if (this.autoUpdate) {
				this.updatePopover();
			}
		});

		this.mutationObserver.observe(this.$refs.popOver as HTMLDivElement, {
			attributes: false,
			childList: true,
			subtree: true
		});
	},
	beforeDestroy() {
		this.removeTargetZIndex(this.target);
		this.resizeObserver?.disconnect();
		this.mutationObserver?.disconnect();
		this.popper?.destroy();
		if (this.frameRequest) {
			window.cancelAnimationFrame(this.frameRequest);
		}
	},
	methods: {
		updatePopover() {
			if (!this.isUpdating) {
				this.isUpdating = true;

				this.frameRequest = window.requestAnimationFrame(async () => {
					try {
						await this.popper?.update();
					} catch (err) {
						// do nothing
					} finally {
						this.isUpdating = false;
						this.frameRequest = null;
					}
				});
			}
		},

		removeTargetZIndex(target: string) {
			if (target) {
				const targetElement = document.querySelector<HTMLElement>(target);
				if (targetElement) {
					targetElement.style.zIndex = "";

					if (this.absoluteTarget) {
						targetElement.style.position = "";
					}
				}
			}
		},
		setTargetZIndex(target: string) {
			if (target) {
				const targetElement = document.querySelector<HTMLElement>(target);
				if (targetElement) {
					targetElement.style.zIndex = "104";

					if (this.absoluteTarget) {
						targetElement.style.position = "absolute";
					}
				}
			}
		},
		async render(recreate = true) {
			this.removeTargetZIndex(this.target);
			if (!this.open) {
				return;
			}

			if (recreate && this.popper) {
				this.popper.destroy();
				this.popper = null;
			}

			if (!this.popper) {
				/**
				 * create popover and hold its reference
				 */
				let targetElement = this.$scopedSlots.default
					? this.$scopedSlots.default?.(null)?.[0].elm
					: null;

				if (this.target) {
					targetElement = document.querySelector(this.target) || this.$slots.default?.[0].elm;

					if (!targetElement) {
						// eslint-disable-next-line no-console
						console.warn("target not found in DOM");
						targetElement = document.body;
					} else {
						this.setTargetZIndex(this.target);
					}
				} else if (!targetElement) {
					throw new Error("Default slot is not present, please specify target prop.");
				}

				this.popper = await createPopper(
					targetElement as Element,
					this.$refs.popOver as HTMLDivElement,
					{
						placement: this.placement,
						strategy: this.strategy,
						modifiers: [
							{
								name: "offset",
								options: {
									offset: [0, 4]
								}
							},
							{
								name: "preventOverflow",
								options: {
									altAxis: true,
									boundary: "document",
									padding: 12
								}
							},
							{
								name: "flip",
								options: {
									fallbackPlacements: this.fallbackPlacements
								}
							},
							{
								name: "computeStyles",
								options: {
									gpuAcceleration: false
								}
							}
						]
					}
				);
			} else {
				this.setTargetZIndex(this.target);
			}

			this.popper.update();
		},

		handleOverlayClick(event: MouseEvent) {
			if (!this.overlay) {
				const val = !this.open;
				this.$emit("update:open", val);
			}
			/**
			 * overlay-click event
			 *
			 * @event overlay-click
			 */
			this.$emit("overlay-click", event);
		},

		handleEscapeClick() {
			if (this.escToClose) {
				this.$emit("update:open", false);
			}
		}
	}
});
</script>
<style lang="scss">
div.flow-popover {
	height: 100%;
	display: flex;
	outline: none;

	> div.flow-popover-overlay {
		content: "";
		position: fixed;
		top: 0px;
		left: 0px;

		background-color: #000;
		transition: opacity 0.1s linear;
		z-index: 103;
		opacity: 0;
		pointer-events: none;
		&[transparent] {
			background-color: transparent;
		}
	}

	div.slab-hierarchy
		> div.slab-container[effects]:hover:not([disabled])[state="default"][type="transparent"],
	div.slab-hierarchy
		> div.slab-container[effects]:hover:not([disabled])[state="default"][type="bottom-border"] {
		background-color: inherit !important;
	}

	.flow-popover-content {
		-moz-box-shadow: 0 0 4px rgba(0, 0, 0, 0.35);
		-webkit-box-shadow: 0 0 4px rgba(0, 0, 0, 0.35);
		box-shadow: 0 0 4px rgba(0, 0, 0, 0.35);
	}
}
div.flow-popover[open] {
	> div.flow-popover-overlay {
		&[open] {
			opacity: 0.4;
			pointer-events: auto;
			width: 100%;
			height: 100%;
		}
	}
	> *:not(div.flow-popover-overlay) {
		z-index: 104;
	}

	.flow-popover-content {
		.flow-picto-container[state="default"][type="solid"],
		.search-input-container > .search-input-box > input,
		.search-input-container,
		div.multiselect > div.multiselect__tags > span.multiselect__single,
		div.multiselect > div.multiselect__tags > input.multiselect__input,
		div.multiselect > div.multiselect__tags,
		div.multiselect > div.multiselect__content-wrapper {
			background-color: var(--gray-400);
		}
		div.multiselect > div.multiselect__content-wrapper {
			border: 1px solid var(--gray-400);
		}
		div.multiselect
			> div.multiselect__content-wrapper
			> ul.multiselect__content
			> li.multiselect__element
			> span.multiselect__option {
			div.slab-hierarchy > div.slab-container > div.slab-content > div.slab-title {
				padding-left: 12px;
			}
		}
	}
}
</style>
